/**
 * Quiz settings, minutes, the change summary and the sources (plan
 * docs/plans/lms-plan.md 8.1 "quiz settings and sources in a third" tab;
 * 5.2 quiz_settings, 6.1 change_summary, 6.3 sources, Appendix E, 18.11;
 * WP-44; R8, R10, R21, G8). A read_ack version has no quiz, so its settings
 * are {} and the quiz block is not shown (5.2).
 */
import { Plus } from 'lucide-react';
import { outlineBtnCls } from '../../../../ui/styles';
import type { CourseSource, FeedbackMode, QuizSettings } from '../../../content/types';
import { FEEDBACK_MODES } from '../../../content/types';
import { Checkbox, NumberInput, RowActions, SelectField, TextArea, TextInput } from './fields';
import { moveItem, removeAt, replaceAt } from './model';
import { cardCls, hintCls, microLabelCls, subheadingCls } from './styles';

const FEEDBACK_LABELS: Record<FeedbackMode, string> = {
  after_each: 'After each question',
  at_end: 'At the end, on the results',
};

export interface SettingsEditorProps {
  hasQuiz: boolean;
  quizSettings: Partial<QuizSettings>;
  onQuizSettings: (settings: Partial<QuizSettings>) => void;
  estimatedMinutes: number | null;
  onEstimatedMinutes: (minutes: number) => void;
  /** standard profile: the minutes follow the lint's estimate unless set here (18.17). */
  minutesFollowEstimate: boolean;
  changeSummary: string;
  onChangeSummary: (summary: string) => void;
  versionNo: number;
  sources: CourseSource[];
  onSources: (sources: CourseSource[]) => void;
  /** 6.3: three for compliance, one for an SOP course, two otherwise. */
  minimumSources: number;
}

function blankSource(): CourseSource {
  return { title: '', publisher: '', url: '', accessed_on: '', note: '' };
}

export function SettingsEditor(props: SettingsEditorProps) {
  const { hasQuiz, quizSettings, onQuizSettings, sources, onSources } = props;
  const qs = quizSettings;
  const set = (patch: Partial<QuizSettings>) => onQuizSettings({ ...qs, ...patch });

  return (
    <div className="space-y-4" data-testid="lms-settings-editor">
      {hasQuiz ? (
        <section className={`${cardCls} space-y-3`} aria-labelledby="lms-quiz-settings-title">
          <h3 id="lms-quiz-settings-title" className={subheadingCls}>
            Quiz settings
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <NumberInput
              label="Pass mark (%)"
              value={typeof qs.pass_mark_pct === 'number' ? qs.pass_mark_pct : null}
              onChange={(v) => v !== null && set({ pass_mark_pct: v })}
              min={1}
              max={100}
              testId="lms-setting-pass-mark"
              hint="80 unless the standard sets another bar (D11); scenarios 100."
            />
            <NumberInput
              label="Questions drawn"
              value={typeof qs.draw_count === 'number' ? qs.draw_count : null}
              onChange={(v) => v !== null && set({ draw_count: v })}
              min={1}
              max={200}
              testId="lms-setting-draw"
            />
            <NumberInput
              label="Attempts per day"
              value={typeof qs.max_attempts_per_day === 'number' ? qs.max_attempts_per_day : null}
              onChange={(v) => set({ max_attempts_per_day: v })}
              min={1}
              max={50}
              allowEmpty
              placeholder="Unlimited"
              hint="Empty means unlimited."
            />
            <NumberInput
              label="Cooldown between attempts (minutes)"
              value={typeof qs.cooldown_minutes === 'number' ? qs.cooldown_minutes : null}
              onChange={(v) => v !== null && set({ cooldown_minutes: v })}
              min={0}
              max={10080}
            />
          </div>
          <SelectField
            className="max-w-xs"
            label="Feedback"
            value={qs.feedback_mode ?? 'after_each'}
            onChange={(v) => set({ feedback_mode: v as FeedbackMode })}
            options={FEEDBACK_MODES.map((m) => ({ value: m, label: FEEDBACK_LABELS[m] }))}
          />
          <div className="grid gap-1 sm:grid-cols-2">
            <Checkbox label="Draw questions in random order" checked={qs.randomize_questions === true} onChange={(v) => set({ randomize_questions: v })} />
            <Checkbox label="Show options in random order" checked={qs.randomize_options === true} onChange={(v) => set({ randomize_options: v })} />
            <Checkbox
              label="Reading must be marked read before the quiz"
              checked={qs.require_reading === true}
              onChange={(v) => set({ require_reading: v })}
            />
            <Checkbox
              label="Passing earns a certificate"
              checked={qs.issue_certificate === true}
              onChange={(v) => set({ issue_certificate: v })}
              testId="lms-setting-certificate"
            />
          </div>
        </section>
      ) : (
        <p className={hintCls}>A read-and-acknowledge course has no quiz: the learner reads the document and signs (5.2, 11.3).</p>
      )}

      <section className={`${cardCls} space-y-3`} aria-labelledby="lms-version-meta-title">
        <h3 id="lms-version-meta-title" className={subheadingCls}>
          This version
        </h3>
        <NumberInput
          className="max-w-xs"
          label="Estimated minutes"
          value={props.estimatedMinutes}
          onChange={(v) => v !== null && props.onEstimatedMinutes(v)}
          min={1}
          max={600}
          testId="lms-setting-minutes"
          hint={
            props.minutesFollowEstimate
              ? "Follows the lint's estimate on every save unless you set it here (slides x 0.75 + reading words / 200 + questions drawn, rounded up to 5)."
              : 'A scenario takes 3 to 6 minutes (6.5).'
          }
        />
        <TextArea
          label={props.versionNo >= 2 ? 'Change summary (required to publish)' : 'Change summary'}
          value={props.changeSummary}
          onChange={props.onChangeSummary}
          rows={3}
          testId="lms-setting-change-summary"
          hint="What changed and why, in plain words. From version 2 on it is required and learners see it first on a retrain (6.1)."
        />
      </section>

      <section className={`${cardCls} space-y-3`} aria-labelledby="lms-sources-title" data-testid="lms-sources-editor">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 id="lms-sources-title" className={subheadingCls}>
            {`Sources (${sources.length})`}
          </h3>
          <span className={hintCls}>
            {`At least ${props.minimumSources}: the regulation or standard, the agency's guidance, a professional body (6.3, G8).`}
          </span>
        </div>
        {sources.map((s, i) => (
          <div key={i} className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <div className="flex items-center justify-between gap-2">
              <span className={microLabelCls}>{`Source ${i + 1}`}</span>
              <RowActions
                index={i}
                count={sources.length}
                label={`source ${i + 1}`}
                onMove={(to) => onSources(moveItem(sources, i, to))}
                onRemove={() => onSources(removeAt(sources, i))}
              />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <TextInput label="Title" value={s.title} onChange={(title) => onSources(replaceAt(sources, i, { ...s, title }))} />
              <TextInput label="Publisher" value={s.publisher} onChange={(publisher) => onSources(replaceAt(sources, i, { ...s, publisher }))} />
              <TextInput
                label="Web address (https)"
                type="url"
                value={s.url ?? ''}
                onChange={(url) => onSources(replaceAt(sources, i, { ...s, url }))}
              />
              <TextInput
                label="Date accessed"
                type="date"
                value={s.accessed_on}
                onChange={(accessed_on) => onSources(replaceAt(sources, i, { ...s, accessed_on }))}
              />
            </div>
            <TextInput label="What it was used for" value={s.note} onChange={(note) => onSources(replaceAt(sources, i, { ...s, note }))} />
          </div>
        ))}
        <button
          type="button"
          className={outlineBtnCls()}
          onClick={() => onSources([...sources, blankSource()])}
          data-testid="lms-add-source"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add source
        </button>
      </section>
    </div>
  );
}

export default SettingsEditor;
